import { useEffect, useRef } from "react";

import type { MobileToWebEventKey } from "@bt-mobile/shared-contracts";

import { subscribeRNEvent } from "./rnEventBridge";

/**
 * Subscribes a React component to a React Native -> WebView event by key.
 *
 * The handler is held in a ref so an inline/unstable handler doesn't tear down
 * and re-create the subscription on every render — the component re-subscribes
 * only when `key` changes.
 *
 * @param key     Event key RN dispatches (e.g. "timeclockDataRefresh"). Reserved
 *   keys autocomplete from the mobile contract; ad-hoc keys are still allowed.
 * @param handler Called with the payload each time RN fires the event.
 *
 * @example
 * ```tsx
 * useRNEvent("timeclockDataRefresh", () => {
 *   void refetch();
 * });
 * ```
 */
export function useRNEvent<T = unknown>(
  key: MobileToWebEventKey,
  handler: (payload: T) => void,
): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    return subscribeRNEvent(key, (payload) => {
      handlerRef.current(payload as T);
    });
  }, [key]);
}
